import React from 'react';
import moment from 'moment';
import { T, FONT_STACK, NUMERIC, money } from '../customer/customers.theme';
import {
  CloseIcon,
  ErrorLine,
  PlusIcon,
  SearchIcon,
  Spinner,
  TrashIcon,
} from '../customer/CustomersUI';
import { Thumbnail } from '../menu/MenuUI';
import { initialsOf } from '../menu/menuApi';
import { readDate } from '../../utils/appDate';
import { COPY, REASONS, reasonRules } from './stock.copy';
import { ArrowInIcon, ArrowOutIcon, BoxIcon } from './StockIcons';
import { statusOf } from './StockList';

/**
 * One delivery, or one issue, in one screen.
 *
 * This is the whole point of the design: a supplier's invoice with ten items
 * on it is ONE document with ten lines, so it is entered as one — picker on
 * the left, lines on the right, one save that posts the lot. The server agrees
 * (`lines[]` on the movement, applied inside a single transaction) and so does
 * the phone, whose footer says "All N lines post together".
 *
 * ── The keyboard ────────────────────────────────────────────────────────────
 * A storekeeper with a paper invoice should never touch the mouse: type part
 * of a name, Enter adds it and lands in its quantity box, Tab moves to the
 * cost, Enter goes back to the search for the next one. Adding something
 * already on the document jumps to its line instead of adding it twice —
 * though the server does allow the same item on two lines, so a line can be
 * added deliberately from the picker.
 *
 * ── What is checked here, and what is not ───────────────────────────────────
 * The header rules below are `stockFunctions.validateHeader`, restated so the
 * form refuses what the server would refuse before a delivery is typed out.
 * The arithmetic is NOT restated: an OUT line is priced by the server at the
 * item's weighted average at the instant it leaves, so the cost shown on an
 * OUT is the current average and the posted line may differ. It is shown
 * greyed for that reason.
 */

const money2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

export default function StockEntryDialog({
  type,
  movement,
  stocks,
  stations,
  vendors,
  currency,
  busy,
  onClose,
  onSubmit,
  onCreateStock,
}) {
  const editing = Boolean(movement);
  const direction = editing ? movement.type : type;
  const isIn = direction === 'in';

  const [reason, setReason] = React.useState(
    () => movement?.reason || (isIn ? 'Purchase' : 'Usage')
  );
  const [pay, setPay] = React.useState(() => movement?.pay || 'cash');
  const [vendor, setVendor] = React.useState(
    () => movement?.vendor?._id || movement?.vendor || ''
  );
  const [station, setStation] = React.useState(
    () => movement?.station?._id || movement?.station || ''
  );
  const [note, setNote] = React.useState(() => movement?.note || '');
  const [date, setDate] = React.useState(() =>
    movement ? readDate(movement.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD')
  );

  /**
   * The time of day, which the date field cannot hold.
   *
   * Every movement was landing at 12:00 AM: the picker gives back a bare
   * `YYYY-MM-DD`, and a bare date is written as midnight. So the day comes
   * from the picker and the clock comes from here — now, for a new document,
   * and its own original time for one being corrected, because an edit must
   * not quietly restamp when the delivery arrived.
   */
  const clock = React.useRef(
    movement ? readDate(movement.date).format('HH:mm:ss.SSS') : moment().format('HH:mm:ss.SSS')
  );

  /**
   * The lines being built.
   *
   * `qty` is kept as the raw string so a half-typed "1." does not become 1 and
   * jump the cursor. It is turned into a number once, on save.
   */
  const [lines, setLines] = React.useState(() =>
    (movement?.lines || []).map((line) => ({
      stock: String(line.stock),
      name: line.name,
      unit: line.unit,
      qty: String(line.qty),
      cost: String(line.cost),
    }))
  );

  const [query, setQuery] = React.useState('');
  const [cursor, setCursor] = React.useState(0);
  const [error, setError] = React.useState('');

  const searchRef = React.useRef(null);
  const qtyRefs = React.useRef({});

  const rules = reasonRules(direction, reason) || {};

  /* ── what the store looks like from in here ───────────────────────────── */

  /**
   * On hand, as it was BEFORE this document.
   *
   * On an edit the movement's own lines have to be taken back off first,
   * otherwise a delivery of 30 that is being corrected to 40 looks as though
   * the shelf already has the 30 — and an issue being corrected looks short.
   */
  const baseQuantity = React.useCallback(
    (stockId) => {
      const item = stocks.find((entry) => entry._id === stockId);
      let quantity = Number(item?.quantity || 0);

      if (editing) {
        (movement.lines || []).forEach((line) => {
          if (String(line.stock) !== stockId) return;
          quantity += movement.type === 'in' ? -Number(line.qty) : Number(line.qty);
        });
      }

      return quantity;
    },
    [stocks, editing, movement]
  );

  const stockById = React.useCallback(
    (stockId) => stocks.find((entry) => entry._id === stockId) || null,
    [stocks]
  );

  const matches = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = needle
      ? stocks.filter((item) => String(item.name || '').toLowerCase().indexOf(needle) !== -1)
      : stocks;
    return rows.slice().sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [stocks, query]);

  /* ── building the document ────────────────────────────────────────────── */

  const focusQty = (stockId) => {
    window.setTimeout(() => {
      const field = qtyRefs.current[stockId];
      if (field) {
        field.focus();
        field.select();
      }
    }, 10);
  };

  const addLine = (item) => {
    setError('');
    setQuery('');
    setCursor(0);

    const existing = lines.find((line) => line.stock === item._id);
    if (existing) {
      focusQty(item._id);
      return;
    }

    setLines((current) => [
      ...current,
      {
        stock: item._id,
        name: item.name,
        unit: item.unit,
        qty: '',
        // An IN suggests the last price paid; an OUT shows the average it will
        // be valued at, which the server owns.
        cost: String(item.cost || 0),
      },
    ]);

    focusQty(item._id);
  };

  const setLineField = (stockId, field, value) => {
    setError('');
    setLines((current) =>
      current.map((line) => (line.stock === stockId ? { ...line, [field]: value } : line))
    );
  };

  const removeLine = (stockId) => {
    setLines((current) => current.filter((line) => line.stock !== stockId));
  };

  const searchKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((value) => Math.min(matches.length - 1, value + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((value) => Math.max(0, value - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (matches[cursor]) addLine(matches[cursor]);
    }
  };

  /* ── totals, and what is stopping the save ────────────────────────────── */

  const filled = lines.filter((line) => Number(line.qty) > 0);
  const total = filled.reduce(
    (sum, line) => sum + Number(line.qty) * (Number(line.cost) || 0),
    0
  );
  const units = filled.reduce((sum, line) => sum + Number(line.qty), 0);

  const missingQty = lines.length - filled.length;

  const short = !isIn && lines.some((line) => baseQuantity(line.stock) - Number(line.qty || 0) < 0);

  const stationName = (stations.find((entry) => entry._id === station) || {}).name || '';

  /** The first thing wrong, in the phone's words. */
  const blocker = React.useMemo(() => {
    if (!lines.length) return COPY.noLines;
    if (missingQty > 0) return COPY.noQuantity(missingQty);
    if (short) return COPY.overIssue;
    if (rules.stationRequired && !station) {
      return stations.length ? COPY.needStation : COPY.noStations;
    }
    if (rules.vendorRequired && !vendor) return COPY.needVendorReturn;
    if (rules.vendorOnCredit && pay === 'credit' && !vendor) return COPY.needVendorCredit;
    if (rules.noteRequired && !note.trim()) return COPY.needNote;
    return '';
  }, [lines.length, missingQty, short, rules, station, stations.length, vendor, pay, note]);

  const submit = async () => {
    if (busy || blocker) {
      if (blocker) setError(blocker);
      return;
    }

    setError('');

    try {
      await onSubmit({
        id: movement?._id,
        type: direction,
        reason,
        // Sent only where the reason uses them — the server drops a vendor on
        // a Usage and forces cash on anything but a purchase, and sending them
        // anyway makes a form field look meaningful when it is not.
        station: rules.stationRequired ? station : '',
        vendor: rules.vendorRequired || rules.vendorOnCredit ? vendor : '',
        pay: isIn ? pay : 'cash',
        note: note.trim(),
        // The chosen day, carrying a real time of day.
        date: `${date}T${clock.current}`,
        lines: filled.map((line) => ({
          stock: line.stock,
          qty: Number(line.qty),
          cost: Number(line.cost) || 0,
        })),
      });
    } catch (requestError) {
      setError(requestError.message || 'Could not save');
    }
  };

  /* ── the chrome ───────────────────────────────────────────────────────── */

  const accent = isIn ? T.paid : reason === 'Waste' ? T.danger : T.brand;
  const tint = isIn ? T.paidTint : reason === 'Waste' ? T.dangerTint : T.selectedBg;

  const field = {
    height: 40,
    padding: '0 12px',
    border: `1px solid ${T.borderStrong}`,
    borderRadius: T.radiusPill,
    background: T.surface,
    font: 'inherit',
    fontFamily: FONT_STACK,
    fontSize: 13.5,
    color: T.ink,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    color: T.inkFaint,
  };

  const cell = (invalid) => ({
    height: 36,
    padding: '0 10px',
    border: `1px solid ${invalid ? T.danger : T.border}`,
    borderRadius: T.radiusControl,
    background: invalid ? T.dangerTint : T.surface,
    font: 'inherit',
    fontFamily: FONT_STACK,
    fontSize: 13.5,
    textAlign: 'right',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  });

  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, busy]);

  React.useEffect(() => {
    window.setTimeout(() => searchRef.current && searchRef.current.focus(), 40);
  }, []);

  return (
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: T.backdrop,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        style={{
          width: 1240,
          maxWidth: '100%',
          height: '88vh',
          display: 'flex',
          flexDirection: 'column',
          background: T.surface,
          borderRadius: T.radiusDialog,
          boxShadow: T.dialog,
          overflow: 'hidden',
        }}
      >
        {/* ── header ─────────────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '16px 20px',
            borderBottom: `1px solid ${T.border}`,
            background: tint,
          }}
        >
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: T.radiusPill,
              background: accent,
              color: T.surface,
            }}
          >
            {isIn ? <ArrowInIcon size={16} /> : <ArrowOutIcon size={16} />}
          </span>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.ink }}>
              {editing ? `${COPY.edit} ${movement.no}` : isIn ? COPY.stockIn : COPY.stockOut}
            </div>
            <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 2 }}>
              {isIn
                ? 'Everything on one delivery or supplier invoice'
                : 'Everything leaving the store together'}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              border: 'none',
              borderRadius: T.radiusControl,
              background: 'transparent',
              color: T.inkFaint,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >
            <CloseIcon />
          </button>
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* ── the picker ───────────────────────────────────────────────── */}
          <div
            style={{
              width: 360,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              background: T.sunk,
              borderRight: `1px solid ${T.border}`,
            }}
          >
            <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  height: 42,
                  padding: '0 14px',
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radiusPill,
                  boxSizing: 'border-box',
                }}
              >
                <span style={{ display: 'flex', color: T.inkFaint }}>
                  <SearchIcon size={15} />
                </span>
                <input
                  ref={searchRef}
                  value={query}
                  placeholder={COPY.searchStock}
                  autoComplete="off"
                  onChange={(event) => {
                    setQuery(event.target.value);
                    setCursor(0);
                  }}
                  onKeyDown={searchKeyDown}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    font: 'inherit',
                    fontFamily: FONT_STACK,
                    fontSize: 13.5,
                    color: T.ink,
                  }}
                />
              </div>

              <div style={{ fontSize: 12, color: T.inkFaint }}>
                {matches.length} of {stocks.length} · {COPY.pickerAdded(lines.length)}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 8px' }}>
              {matches.map((item, position) => {
                const added = lines.some((line) => line.stock === item._id);
                const state = statusOf(item);

                return (
                  <div
                    key={item._id}
                    onMouseDown={(event) => {
                      // mousedown, not click: the search box must not lose
                      // focus before the line is added.
                      event.preventDefault();
                      addLine(item);
                    }}
                    onMouseEnter={() => setCursor(position)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: 8,
                      borderRadius: T.radiusControl,
                      border: `1px solid ${position === cursor ? T.border : 'transparent'}`,
                      background: position === cursor ? T.surface : 'transparent',
                      opacity: added ? 0.55 : 1,
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ width: 36, flexShrink: 0 }}>
                      <Thumbnail
                        src={item.imageUrl}
                        initials={initialsOf(item.name)}
                        height={36}
                        radius={8}
                      />
                    </span>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13.5,
                          fontWeight: 600,
                          color: T.ink,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {item.name}
                      </div>
                      <div style={{ fontSize: 12, color: T.inkFaint, ...NUMERIC }}>
                        {COPY.onHand(baseQuantity(item._id), item.unit)} · {money(item.cost, currency)}
                        {state !== 'ok' ? (
                          <span
                            style={{
                              marginLeft: 6,
                              fontWeight: 700,
                              color: state === 'out' ? T.danger : T.pendingInk,
                            }}
                          >
                            {state === 'out' ? COPY.outBadge : COPY.lowBadge}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        flexShrink: 0,
                        borderRadius: T.radiusPill,
                        border: `1px solid ${added ? T.ink : T.border}`,
                        background: added ? T.ink : T.surface,
                        color: added ? T.surface : T.inkSoft,
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {added ? '✓' : <PlusIcon size={12} />}
                    </span>
                  </div>
                );
              })}

              {!matches.length ? (
                <div style={{ padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 13, color: T.inkSoft, marginBottom: 10 }}>
                    {COPY.nothingCalled(query.trim())}
                  </div>
                  {onCreateStock ? (
                    <button
                      type="button"
                      onClick={() => onCreateStock(query.trim())}
                      style={{
                        height: 34,
                        padding: '0 14px',
                        border: `1px dashed ${T.borderStrong}`,
                        borderRadius: T.radiusPill,
                        background: T.surface,
                        font: 'inherit',
                        fontSize: 13,
                        fontWeight: 600,
                        color: T.ink,
                        cursor: 'pointer',
                      }}
                    >
                      {COPY.createIt}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                padding: '8px 14px',
                borderTop: `1px solid ${T.border}`,
                fontSize: 11,
                color: T.inkFaint,
              }}
            >
              <span>↑ ↓ move</span>
              <span>Enter adds</span>
              <span>Tab to cost</span>
              <span>Enter returns to search</span>
            </div>
          </div>

          {/* ── the document ─────────────────────────────────────────────── */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div
              style={{
                display: 'flex',
                gap: 14,
                flexWrap: 'wrap',
                padding: '14px 20px',
                borderBottom: `1px solid ${T.hairline}`,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={labelStyle}>{COPY.reason}</span>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {REASONS[direction].map((entry) => {
                    const active = entry.value === reason;
                    return (
                      <button
                        key={entry.value}
                        type="button"
                        onClick={() => {
                          setReason(entry.value);
                          setError('');
                        }}
                        style={{
                          height: 32,
                          padding: '0 13px',
                          border: `1px solid ${active ? accent : T.border}`,
                          borderRadius: T.radiusPill,
                          background: active ? accent : T.surface,
                          font: 'inherit',
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: active ? T.surface : T.inkSoft,
                          cursor: 'pointer',
                        }}
                      >
                        {entry.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Only a purchase can be on credit: the server forces everything
                  else to cash, so the choice is only offered where it means
                  something. */}
              {isIn && rules.pays ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={labelStyle}>{COPY.payment}</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[
                      { value: 'cash', label: COPY.cash },
                      { value: 'credit', label: COPY.onCredit },
                    ].map((entry) => {
                      const active = entry.value === pay;
                      return (
                        <button
                          key={entry.value}
                          type="button"
                          onClick={() => {
                            setPay(entry.value);
                            setError('');
                          }}
                          style={{
                            height: 32,
                            padding: '0 13px',
                            border: `1px solid ${active ? T.ink : T.border}`,
                            borderRadius: T.radiusPill,
                            background: active ? T.ink : T.surface,
                            font: 'inherit',
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: active ? T.surface : T.inkSoft,
                            cursor: 'pointer',
                          }}
                        >
                          {entry.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {rules.vendorRequired || rules.vendorOnCredit ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 210 }}>
                  <span style={labelStyle}>
                    {COPY.vendor}{' '}
                    <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                      {rules.vendorRequired || pay === 'credit' ? COPY.required : COPY.vendorOptional}
                    </span>
                  </span>
                  <select
                    value={vendor}
                    onChange={(event) => {
                      setVendor(event.target.value);
                      setError('');
                    }}
                    style={{ ...field, cursor: 'pointer' }}
                  >
                    <option value="">—</option>
                    {vendors.map((entry) => (
                      <option key={entry._id} value={entry._id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {rules.stationRequired ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 190 }}>
                  <span style={labelStyle}>
                    {COPY.station}{' '}
                    <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                      {COPY.required}
                    </span>
                  </span>
                  <select
                    value={station}
                    onChange={(event) => {
                      setStation(event.target.value);
                      setError('');
                    }}
                    style={{ ...field, cursor: 'pointer' }}
                  >
                    <option value="">—</option>
                    {stations.map((entry) => (
                      <option key={entry._id} value={entry._id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 200 }}>
                <span style={labelStyle}>
                  {isIn ? COPY.invoiceNote : COPY.note}{' '}
                  {rules.noteRequired ? (
                    <span style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                      {COPY.required}
                    </span>
                  ) : null}
                </span>
                <input
                  value={note}
                  placeholder={isIn ? COPY.invoiceNoteHint : COPY.outNoteHint}
                  onChange={(event) => {
                    setNote(event.target.value);
                    setError('');
                  }}
                  style={field}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: 160 }}>
                <span style={labelStyle}>{COPY.date}</span>
                <input
                  type="date"
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                  style={field}
                />
              </div>
            </div>

            {/* the lines */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
              {!lines.length ? (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    textAlign: 'center',
                    color: T.inkSoft,
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 56,
                      height: 56,
                      border: `1.5px dashed ${T.borderStrong}`,
                      borderRadius: T.radiusPill,
                      color: T.inkGhost,
                    }}
                  >
                    <BoxIcon />
                  </span>
                  <b style={{ color: T.ink }}>{COPY.noItemsYet}</b>
                  <span style={{ fontSize: 12.5 }}>
                    {COPY.addItems} — search on the left and press Enter.
                  </span>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {[
                        { label: '#', width: 32, align: 'left' },
                        { label: 'Item', align: 'left' },
                        { label: COPY.quantity, width: 150, align: 'right' },
                        { label: isIn ? 'Cost / unit' : 'Cost', width: 140, align: 'right' },
                        { label: COPY.lineTotal, width: 120, align: 'right' },
                        { label: COPY.leftAfter, width: 130, align: 'right' },
                        { label: '', width: 44, align: 'right' },
                      ].map((column, position) => (
                        <th
                          key={`${column.label}-${position}`}
                          style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            width: column.width,
                            padding: '12px 8px 8px',
                            textAlign: column.align,
                            background: T.surface,
                            borderBottom: `1px solid ${T.border}`,
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            textTransform: 'uppercase',
                            color: T.inkFaint,
                          }}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {lines.map((line, position) => {
                      const item = stockById(line.stock);
                      const before = baseQuantity(line.stock);
                      const quantity = Number(line.qty) || 0;
                      const after = isIn ? before + quantity : before - quantity;
                      const bad = !isIn && after < 0;

                      return (
                        <tr key={line.stock}>
                          <td style={{ padding: '7px 8px', borderBottom: `1px solid ${T.hairline}`, color: T.inkFaint, fontSize: 12.5 }}>
                            {position + 1}
                          </td>

                          <td style={{ padding: '7px 8px', borderBottom: `1px solid ${T.hairline}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ width: 30, flexShrink: 0 }}>
                                <Thumbnail
                                  src={item?.imageUrl}
                                  initials={initialsOf(line.name)}
                                  height={30}
                                  radius={6}
                                />
                              </span>
                              <span>
                                <span style={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
                                  {line.name}
                                </span>
                                <div style={{ fontSize: 12, color: T.inkFaint, ...NUMERIC }}>
                                  {COPY.onHand(before, line.unit)}
                                </div>
                              </span>
                            </div>
                          </td>

                          <td style={{ padding: '7px 8px', borderBottom: `1px solid ${T.hairline}` }}>
                            <div style={{ position: 'relative' }}>
                              <input
                                ref={(node) => {
                                  qtyRefs.current[line.stock] = node;
                                }}
                                type="number"
                                min="0"
                                value={line.qty}
                                placeholder="0"
                                onChange={(event) => setLineField(line.stock, 'qty', event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key !== 'Enter') return;
                                  event.preventDefault();
                                  if (isIn) {
                                    const costField = qtyRefs.current[`cost-${line.stock}`];
                                    if (costField) {
                                      costField.focus();
                                      costField.select();
                                      return;
                                    }
                                  }
                                  if (searchRef.current) searchRef.current.focus();
                                }}
                                style={{ ...cell(bad), paddingRight: 34 }}
                              />
                              <span
                                style={{
                                  position: 'absolute',
                                  right: 10,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  fontSize: 11,
                                  color: T.inkFaint,
                                  pointerEvents: 'none',
                                }}
                              >
                                {line.unit}
                              </span>
                            </div>
                          </td>

                          <td style={{ padding: '7px 8px', borderBottom: `1px solid ${T.hairline}` }}>
                            {isIn ? (
                              <input
                                ref={(node) => {
                                  qtyRefs.current[`cost-${line.stock}`] = node;
                                }}
                                type="number"
                                min="0"
                                step="0.01"
                                value={line.cost}
                                onChange={(event) => setLineField(line.stock, 'cost', event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key !== 'Enter') return;
                                  event.preventDefault();
                                  if (searchRef.current) searchRef.current.focus();
                                }}
                                style={cell(false)}
                              />
                            ) : (
                              // The server prices an issue at the average at the
                              // moment it leaves, so this is what it will most
                              // likely be — not something to type over.
                              <div
                                style={{
                                  textAlign: 'right',
                                  paddingRight: 10,
                                  fontSize: 13,
                                  color: T.inkFaint,
                                  ...NUMERIC,
                                }}
                              >
                                {money(line.cost, currency)}
                              </div>
                            )}
                          </td>

                          <td
                            style={{
                              padding: '7px 8px',
                              borderBottom: `1px solid ${T.hairline}`,
                              textAlign: 'right',
                              fontWeight: 700,
                              ...NUMERIC,
                            }}
                          >
                            {money(money2(quantity * (Number(line.cost) || 0)), currency)}
                          </td>

                          <td
                            style={{
                              padding: '7px 8px',
                              borderBottom: `1px solid ${T.hairline}`,
                              textAlign: 'right',
                              fontSize: 12.5,
                              ...NUMERIC,
                            }}
                          >
                            <b
                              style={{
                                color: bad
                                  ? T.danger
                                  : item && Number(item.reorderLevel || 0) > 0 && after <= Number(item.reorderLevel)
                                  ? T.pendingInk
                                  : T.ink,
                              }}
                            >
                              {money2(after)}
                            </b>{' '}
                            <span style={{ color: T.inkFaint }}>{line.unit}</span>
                            {bad ? (
                              <div style={{ color: T.danger, fontWeight: 600 }}>
                                {COPY.onlyOnHand(before, line.unit)}
                              </div>
                            ) : null}
                          </td>

                          <td
                            style={{
                              padding: '7px 8px',
                              borderBottom: `1px solid ${T.hairline}`,
                              textAlign: 'right',
                            }}
                          >
                            <button
                              type="button"
                              title="Remove line"
                              onClick={() => removeLine(line.stock)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 30,
                                height: 30,
                                border: `1px solid ${T.border}`,
                                borderRadius: T.radiusControl,
                                background: T.surface,
                                color: T.inkSoft,
                                cursor: 'pointer',
                              }}
                            >
                              <TrashIcon />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* the footer: what it comes to, and the one button that posts it */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 22,
                padding: '14px 20px',
                borderTop: `1px solid ${T.border}`,
                background: T.sunk,
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={labelStyle}>Lines</span>
                <b style={{ fontSize: 18, ...NUMERIC }}>{lines.length}</b>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={labelStyle}>Units</span>
                <b style={{ fontSize: 18, ...NUMERIC }}>{money2(units)}</b>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={labelStyle}>
                  {isIn ? COPY.invoiceTotal : reason === 'Waste' ? COPY.valueLost : COPY.valueOut}
                </span>
                <b style={{ fontSize: 22, color: accent, ...NUMERIC }}>
                  {money(money2(total), currency)}
                </b>
              </div>

              <div style={{ flex: 1, textAlign: 'right', minWidth: 0 }}>
                {error ? (
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ErrorLine>{error}</ErrorLine>
                  </div>
                ) : (
                  <span
                    style={{
                      fontSize: 12.5,
                      color: blocker ? T.pendingInk : T.inkFaint,
                    }}
                  >
                    {blocker || (lines.length ? COPY.postTogether(lines.length) : '')}
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                style={{
                  height: 44,
                  padding: '0 20px',
                  border: `1px solid ${T.borderStrong}`,
                  borderRadius: T.radiusPill,
                  background: T.surface,
                  font: 'inherit',
                  fontSize: 14,
                  fontWeight: 600,
                  color: T.inkSoft,
                  cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >
                {COPY.cancel}
              </button>

              <button
                type="button"
                onClick={submit}
                disabled={busy || Boolean(blocker)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 44,
                  padding: '0 26px',
                  border: 'none',
                  borderRadius: T.radiusPill,
                  background: accent,
                  font: 'inherit',
                  fontSize: 14,
                  fontWeight: 700,
                  color: T.surface,
                  opacity: busy || blocker ? 0.45 : 1,
                  cursor: busy || blocker ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? <Spinner /> : null}
                {COPY.submitLabel({
                  type: direction,
                  reason,
                  stationName,
                  editingNo: editing ? movement.no : '',
                })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
